import React, { useState, useEffect } from 'react';
import { UtensilsCrossed, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

function RestaurationPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [newMeal, setNewMeal] = useState({ date: '', meal_type: 'dejeuner', menu: '', notes: '' });

  const formatDateStr = (d) => { const dd = new Date(d); return dd.getFullYear() + '-' + String(dd.getMonth() + 1).padStart(2, '0') + '-' + String(dd.getDate()).padStart(2, '0'); };
  const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

  useEffect(() => { fetchData(); }, [currentDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const startDate = formatDateStr(addDays(currentDate, -3));
      const endDate = formatDateStr(addDays(currentDate, 3));
      const response = await api.get('/meal-plans', { params: { start_date: startDate, end_date: endDate } });
      setMealPlans(response.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newMeal.date || !newMeal.menu) { toast.error('Date et menu requis'); return; }
    setFormLoading(true);
    try {
      await api.post('/meal-plans', newMeal);
      toast.success('Menu planifie');
      setShowDialog(false);
      setNewMeal({ date: '', meal_type: 'dejeuner', menu: '', notes: '' });
      fetchData();
    } catch (e) { toast.error('Erreur'); }
    finally { setFormLoading(false); }
  };

  const getMealTypeLabel = (t) => ({ petit_dejeuner: 'Petit-dej', dejeuner: 'Dejeuner', diner: 'Diner', gouter: 'Gouter' }[t] || t);
  const getMealTypeIcon = (t) => ({ petit_dejeuner: '🌅', dejeuner: '☀️', diner: '🌙', gouter: '🍪' }[t] || '🍽️');
  const getMealsForDate = (date) => mealPlans.filter(m => m.date === date);

  const weekDays = [];
  for (let i = -3; i <= 3; i++) weekDays.push(addDays(currentDate, i));

  const formatMonthYear = (d) => new Date(d).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const formatDayName = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short' });
  const formatDayNum = (d) => new Date(d).getDate();

  return (
    <div className="space-y-6" data-testid="restauration-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Restauration</h1><p className="text-muted-foreground text-sm">Planification des repas</p></div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild><Button className="rounded-full"><Plus className="w-4 h-4 mr-2" />Planifier un repas</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Planifier un repas</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Date *</Label><Input type="date" value={newMeal.date} onChange={(e) => setNewMeal({...newMeal, date: e.target.value})} /></div>
                <div className="space-y-2"><Label>Type *</Label><Select value={newMeal.meal_type} onValueChange={(v) => setNewMeal({...newMeal, meal_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="petit_dejeuner">Petit-dejeuner</SelectItem><SelectItem value="dejeuner">Dejeuner</SelectItem><SelectItem value="diner">Diner</SelectItem><SelectItem value="gouter">Gouter</SelectItem></SelectContent></Select></div>
              </div>
              <div className="space-y-2"><Label>Menu *</Label><Textarea value={newMeal.menu} onChange={(e) => setNewMeal({...newMeal, menu: e.target.value})} rows={3} placeholder="Decrire le menu..." /></div>
              <div className="space-y-2"><Label>Notes</Label><Input value={newMeal.notes} onChange={(e) => setNewMeal({...newMeal, notes: e.target.value})} placeholder="Notes..." /></div>
              <div className="flex justify-end gap-2 pt-4"><Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button><Button type="submit" disabled={formLoading}>{formLoading ? 'Planification...' : 'Planifier'}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCurrentDate(addDays(currentDate, -7))}><ChevronLeft className="w-4 h-4" /></Button>
            <h2 className="text-lg font-semibold capitalize">{formatMonthYear(currentDate)}</h2>
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCurrentDate(addDays(currentDate, 7))}><ChevronRight className="w-4 h-4" /></Button>
          </div>

          {loading ? (<div className="grid grid-cols-7 gap-2">{[1,2,3,4,5,6,7].map(i => <Skeleton key={i} className="h-40" />)}</div>) : (
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((day) => {
                const dateStr = formatDateStr(day);
                const dayMeals = getMealsForDate(dateStr);
                const isToday = dateStr === formatDateStr(new Date());
                return (
                  <div key={dateStr} className={'p-3 rounded-lg border min-h-[160px] ' + (isToday ? 'border-primary bg-primary/5' : '')}>
                    <div className="text-center mb-2">
                      <p className="text-xs text-muted-foreground uppercase">{formatDayName(day)}</p>
                      <p className={'text-lg font-bold ' + (isToday ? 'text-primary' : '')}>{formatDayNum(day)}</p>
                    </div>
                    <div className="space-y-1.5">
                      {['petit_dejeuner', 'dejeuner', 'diner'].map((mealType) => {
                        const meal = dayMeals.find(m => m.meal_type === mealType);
                        return (
                          <div key={mealType} className={'p-1.5 rounded text-xs ' + (meal ? 'bg-green-100' : 'bg-muted/50')}>
                            <span className="mr-1">{getMealTypeIcon(mealType)}</span>
                            {meal ? <span className="truncate">{meal.menu.substring(0, 15)}...</span> : <span className="text-muted-foreground">-</span>}
                          </div>
                        );
                      })}
                    </div>
                    <Button variant="ghost" size="sm" className="w-full mt-2 h-6 text-xs" onClick={() => { setNewMeal({...newMeal, date: dateStr}); setShowDialog(true); }}><Plus className="w-3 h-3 mr-1" />Ajouter</Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Prochains repas</CardTitle></CardHeader>
        <CardContent>
          {mealPlans.length === 0 ? (
            <div className="text-center py-8"><UtensilsCrossed className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" /><p className="text-muted-foreground">Aucun repas planifie</p></div>
          ) : (
            <div className="space-y-3">
              {mealPlans.slice(0, 10).map((meal) => (
                <div key={meal.id} className="flex items-start gap-4 p-3 bg-muted/50 rounded-lg">
                  <div className="text-2xl">{getMealTypeIcon(meal.meal_type)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><Badge variant="outline">{meal.date}</Badge><Badge>{getMealTypeLabel(meal.meal_type)}</Badge></div>
                    <p className="text-sm">{meal.menu}</p>
                    {meal.notes && <p className="text-xs text-muted-foreground mt-1">{meal.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
export default RestaurationPage;
